<template>
  <div>
    <b-overlay
      id="overlay-background"
      :show="loader"
      variant="dark"
      opacity="0.85"
      blur="2px"
      spinner-variant="primary"
      spinner-small
      rounded="sm"
    >
      <alert-handler :alert-errors="alertErrors" />
      <b-card
        aria-hidden="true"
        class="mb-0"
        title="Personal Information"
      >
        <validation-observer
          #default="{ handleSubmit }"
          ref="refFormObserver"
        >

          <!-- User Info: Input Fields -->
          <b-form
            @submit.prevent="handleSubmit(onSubmit)"
            @reset.prevent="resetForm"
          >
            <b-row>

              <!-- Field: Username -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Username"
                  data-vv-validate-on="blur"
                  rules="required|alpha-num"
                >
                  <b-form-group

                    :label="$t('DEBTIA_USERNAME')"
                    label-for="username"
                  >
                    <b-form-input
                      id="username"
                      v-model="userData.username"
                      :state="getValidationState(validationContext)"
                      trim
                      :maxlength="maxCharactersAllowed"
                      @input="characterLeft"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                    <small
                      :style="maxCharacters > 10 ? 'color: green' : 'color: red'"
                    >
                      {{ maxCharacters }} characters left out of {{ maxCharactersAllowed }}
                    </small>
                    <span
                      v-if="!validateUsername"
                      style="color:#ea5455"
                    >
                      Username is not valid
                    </span>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Full Name -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Name"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_NAME')"
                    label-for="full-name"
                  >
                    <b-form-input
                      id="full-name"
                      v-model="userData.name"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Email -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Email"
                  rules="required|email"
                >
                  <b-form-group
                    :label="$t('DEBTIA_EMAIL')"
                    label-for="email"
                  >
                    <b-form-input
                      id="email"
                      v-model="userData.email"
                      type="email"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Phone -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Phone"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_PHONE')"
                    label-for="phone"
                  >
                    <b-form-input
                      id="phone"
                      v-model="userData.phone"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Password -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Password"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_PASSWORD')"
                    label-for="password"
                  >
                    <b-input-group
                      class="input-group-merge"
                      :class="errors.length > 0 ? 'is-invalid':null"
                    >
                      <b-form-input
                        id="login-password"
                        v-model="userData.password"
                        :state="errors.length > 0 ? false:null"
                        class="form-control-merge"
                        :type="passwordFieldType"
                        name="login-password"
                        placeholder="············"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          class="cursor-pointer"
                          :icon="passwordToggleIcon"
                          @click="togglePasswordVisibility"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Status -->
              <b-col
                cols="12"
                md="4"
              >

                <b-form-group
                  :label="$t('DEBTIA_LOGIN')"
                  label-for="login-status"
                >
                  <v-select
                    v-model="userData.loginStatus"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="loginOptions"
                    :reduce="val => val.value"
                    :clearable="false"
                    input-id="login-status"
                  />
                </b-form-group>
              </b-col>

              <!-- Field: Billing Email -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_BILLING_EMAIL')"
                  label-for="billing-email"
                >
                  <b-form-input
                    id="billing-email"
                    v-model="userData.billing_email"
                    trim
                  />
                </b-form-group>
              </b-col>

              <!-- Field: Contact Person -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_CONTACT_PERSON')"
                  label-for="contact-person"
                >
                  <b-form-input
                    id="contact-person"
                    v-model="userData.contact_person"
                    trim
                  />
                </b-form-group>
              </b-col>

              <!-- Field: Client Type -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_CLIENT_TYPE')"
                  label-for="client-type"
                >
                  <v-select
                    v-model="userData.type"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="typeOptions"
                    :reduce="val => val.value"
                    :clearable="false"
                    input-id="client-type"
                    :value="'person'"
                  />
                </b-form-group>
              </b-col>

              <!-- Field: CVRCPR -->
              <b-col
                v-if="userData.type !== 'person'"
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="CVR"
                  rules="integer|length:8"
                >
                  <b-form-group
                    :label="$t('DEBTIA_CVRCPR')"
                    label-for="cvrcpr"
                  >
                    <b-form-input
                      id="cvrcpr"
                      v-model="userData.cvrcpr"
                      :state="getValidationState(validationContext)"
                      trim
                      maxlength="8"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>

              </b-col>
              <!-- Field: CPR -->
              <b-col
                v-if="userData.type === 'person'"
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_CPR')"
                  label-for="cpr"
                >
                  <cleave
                    id="cpr"
                    v-model="userData.cpr"
                    class="form-control"
                    :raw="false"
                    :options="options.cprDelimiter"
                  />
                </b-form-group>

              </b-col>

              <!-- Field: TERMSHEETCODE -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  class="classformgroup"
                  :label="$t('DEBTIA_TERMSHEETCODE')"
                  label-for="debtor-login-status"
                >
                  <v-select
                    v-model="selectedTermSheetCode"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="termSheetCodeOptions"
                    :reduce="val => val.value"
                    :clearable="false"
                    input-id="debtor-login-status"
                  />
                </b-form-group>
              </b-col>
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group :label="$t('DEBTIA_COLLECTION_TYPE')">
                  <b-form-radio-group
                    v-model="userData.collection_type"
                    :options="collectiontypeoptions"
                    class="demo-inline-spacing"
                    name="radio-inline"
                  />
                </b-form-group>
              </b-col>
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  class="classformgroup"
                  :label="$t('DEBTIA_MONITORING_TERMSHEETCODE')"
                  label-for="monitoring_termsheetcode"
                >
                  <v-select
                    v-model="selectedoMonitoringTermSheetCode"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="[{label: 'Select Monitoring Termsheet', value: ''}, ...termSheetCodeOptions]"
                    :reduce="val => val.value"
                    :clearable="false"
                    input-id="monitoring_termsheetcode"
                  />
                </b-form-group>
              </b-col>
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="RKI Customer ID"
                  rules="integer"
                >
                  <b-form-group
                    :label="$t('DEBTIA_RKI_CUSTOMER_NUMBER')"
                    label-for="rki-customer-number"
                  >
                    <b-form-input
                      id="rki-customer-id"
                      v-model="userData.rki_customer_id"
                      :state="getValidationState(validationContext)"
                      trim
                      maxlength="10"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
            </b-row>

            <hr
              v-if="userData.type !== 'person'"
            >

            <b-row
              v-if="userData.type === 'sole-proprietorship' || userData.type === 'capital-company'"
            >
              <b-col
                cols="12"
                md="12"
              ><h4>{{ $t('DEBTIA_VAT_EXEMPTION') }}</h4></b-col>

              <b-col
                cols="12"
                md="4"
              >
                <b-form-checkbox
                  v-model="userData.vat_exempted"
                  checked="false"
                  class="custom-control-primary"
                  name="check-button"
                  switch
                >
                  <span class="switch-icon-left">
                    <feather-icon icon="CheckIcon" />
                  </span>
                  <span class="switch-icon-right">
                    <feather-icon icon="XIcon" />
                  </span>
                </b-form-checkbox>
              </b-col>
            </b-row>
            <hr>

            <b-row>
              <b-col
                cols="12"
                md="12"
              ><h4>{{ $t('DEBTIA_BANK_INFORMATION') }}</h4></b-col>

              <b-col
                cols="12"
                md="4"
              >

                <b-form-group
                  :label="$t('DEBTIA_BANK_BRANCH_NO')"
                  label-for="bankBranchNo"
                >
                  <b-form-input
                    id="bankBranchNo"
                    v-model="userData.bankBranchNo"
                    trim
                  />
                </b-form-group>
              </b-col>

              <b-col
                cols="12"
                md="4"
              >

                <b-form-group
                  :label="$t('DEBTIA_BANK_ACCOUNT_NO')"
                  label-for="bankAccountNo"
                >
                  <b-form-input
                    id="bankAccountNo"
                    v-model="userData.bankAccountNo"
                    trim
                  />
                </b-form-group>
              </b-col>

              <b-col
                cols="12"
                md="4"
              >

                <b-form-group
                  :label="$t('DEBTIA_IBAN')"
                  label-for="iban"
                >
                  <b-form-input
                    id="iban"
                    v-model="userData.iban"
                    trim
                  />
                </b-form-group>
              </b-col>

              <b-col
                cols="12"
                md="4"
              >

                <b-form-group
                  :label="$t('DEBTIA_SWIFTCODE')"
                  label-for="swiftCode"
                >
                  <b-form-input
                    id="swiftCode"
                    v-model="userData.swiftCode"
                    trim
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <hr>

            <b-row>
              <b-col
                cols="12"
                md="12"
              ><h4>{{ $t('DEBTIA_ADDRESS') }}</h4></b-col>

              <!-- Field: House Number -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="House Number"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_HOUSE_NUMBER')"
                    label-for="house-number"
                  >
                    <b-form-input
                      id="house-number"
                      v-model="userData.address.house_number"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: House Number -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Road Name"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_ROAD_NAME')"
                    label-for="road-name"
                  >
                    <b-form-input
                      id="road-name"
                      v-model="userData.address.road_name"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Place -->
              <b-col
                cols="12"
                md="4"
              >

                <b-form-group
                  :label="$t('DEBTIA_PLACE_NAME')"
                  label-for="place"
                >
                  <b-form-input
                    id="place"
                    v-model="userData.address.place"
                    trim
                  />
                </b-form-group></b-col>

              <!-- Field: Zipcode -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Zip Code"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_ZIP_CODE')"
                    label-for="zip-code"
                  >
                    <b-form-input
                      id="zip-code"
                      v-model="userData.address.zip_code"
                      :state="getValidationState(validationContext)"
                      trim
                      @change="getCityName('standard_address', $event)"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: City -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="City"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_CITY')"
                    label-for="city"
                  >
                    <b-form-input
                      id="city"
                      v-model="userData.address.city"
                      :state="getValidationState(validationContext)"
                      trim
                      @change="getZipCode('standard_address', $event)"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Country -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  v-slot="{ errors, validated, failed }"
                  name="Country"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_COUNTRY')"
                    label-for="select-country"
                    :state="failed ? false : (validated ? true : null)"
                  >
                    <v-select
                      id="select-country"
                      v-model="userData.address.country_id"
                      :disabled="busy"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="countries"
                      :reduce="val => val.value"
                      :clearable="true"
                      placeholder="Select Country"
                      :class="{ 'is-invalid': errors.length > 0 }"
                    >
                      <template #selected-option-container="{ option }">
                        <div class="vs__selected">
                          {{ $t(option.label) }}
                        </div>
                      </template>

                      <template #option="{ label }">
                        {{ $t(label) }}
                      </template>
                    </v-select>

                    <b-form-invalid-feedback v-if="errors[0]">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

            </b-row>

            <hr>

            <b-row>
              <b-col
                cols="12"
                md="12"
              ><h4>{{ $t('DEBTIA_BILLING_ADDRESS') }}</h4></b-col>

              <!-- Field: House Number -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Billing House Number"
                  :rules="addressRequired() ? 'required' : ''"
                >
                  <b-form-group
                    :label="$t('DEBTIA_HOUSE_NUMBER')"
                    label-for="billing-house-number"
                  >
                    <b-form-input
                      id="billing-house-number"
                      v-model="userData.billing_address.house_number"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: House Number -->
              <b-col
                cols="12"
                md="4"
              >

                <b-form-group
                  :label="$t('DEBTIA_ROAD_NAME')"
                  label-for="billing-road-name"
                >
                  <validation-provider
                    #default="validationContext"
                    name="Billing Road Name"
                    :rules="addressRequired() ? 'required' : ''"
                  >
                    <b-form-input
                      id="billing-road-name"
                      v-model="userData.billing_address.road_name"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Field: Place -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_PLACE_NAME')"
                  label-for="billing-place"
                >
                  <validation-provider
                    #default="validationContext"
                    name="Billing Place"
                    :rules="addressRequired() ? 'required' : ''"
                  >
                    <b-form-input
                      id="billing-place"
                      v-model="userData.billing_address.place"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Field: Zipcode -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_ZIP_CODE')"
                  label-for="billing-zip-code"
                >
                  <validation-provider
                    #default="validationContext"
                    name="Billing Zip Code"
                    :rules="addressRequired() ? 'required' : ''"
                  >
                    <b-form-input
                      id="billing-zip-code"
                      v-model="userData.billing_address.zip_code"
                      :state="getValidationState(validationContext)"
                      trim
                      @change="getCityName('billing_address', $event)"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Field: City -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_CITY')"
                  label-for="billing-city"
                >
                  <validation-provider
                    #default="validationContext"
                    name="Billing City"
                    :rules="addressRequired() ? 'required' : ''"
                  >
                    <b-form-input
                      id="billing-city"
                      v-model="userData.billing_address.city"
                      :state="getValidationState(validationContext)"
                      trim
                      @change="getZipCode('billing_address', $event)"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Field: Country -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_COUNTRY')"
                  label-for="billing-country"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Billing Country"
                    :rules="addressRequired() ? 'required' : ''"
                  >
                    <v-select
                      v-model="userData.billing_address.country_id"
                      :disabled="busy"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="countries"
                      :reduce="val => val.value"
                      :clearable="false"
                      input-id="client_billing_address_country"
                      :value="'Select Country'"
                    >
                      <template
                        #selected-option-container="{ option }"
                      >
                        <div class="vs__selected">
                          {{ $t(option.label) }}</div>
                      </template>
                      <template
                        slot="option"
                        slot-scope="option"
                      >
                        {{ $t(option.label) }}
                      </template>
                    </v-select>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

            </b-row>

            <hr>

            <!-- Action Buttons -->
            <b-button
              variant="primary"
              class="mb-1 mb-sm-0 mr-0 mr-sm-1"
              :block="$store.getters['app/currentBreakPoint'] === 'xs'"
              type="submit"
            >
              {{ $t('DEBTIA_SAVE') }}
            </b-button>
            <b-button
              variant="outline-secondary"
              type="reset"
              :block="$store.getters['app/currentBreakPoint'] === 'xs'"
            >
              {{ $t('DEBTIA_RESET') }}
            </b-button>
          </b-form>
        </validation-observer>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BButton,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BForm,
  BCard,
  BFormInvalidFeedback,
  BOverlay,
  BFormCheckbox,
  BInputGroupAppend,
  BInputGroup,
  BFormRadioGroup,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import formValidation from '@core/comp-functions/forms/form-validation'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import { ref, reactive } from '@vue/composition-api'
import axios from '@axios'
import store from '@/store'

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { required, alphaNum, email } from '@validations'
import router from '@/router'
import $this from 'vue'
import Cleave from 'vue-cleave-component'
import countries from '@/countries'
import i18n from '@/libs/i18n'
// eslint-disable-next-line import/no-extraneous-dependencies
import 'cleave.js/dist/addons/cleave-phone.us'
import constants from '@/app-configs/constants'
import clientStoreModule from '../clientStoreModule'
import AlertHandler from './AlertHandler.vue'

export default {
  components: {
    BButton,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BCard,
    vSelect,
    ValidationProvider,
    ValidationObserver,
    BFormInvalidFeedback,
    AlertHandler,
    BOverlay,
    BFormCheckbox,
    Cleave,
    BInputGroupAppend,
    BInputGroup,
    BFormRadioGroup,
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      busy: false,
      // userType: JSON.parse(localStorage.getItem('userData')),
      countries,
      required,
      alphaNum,
      email,
      termSheetCodeLoader: false,
      selectedTermSheetCode: '',
      selectedoMonitoringTermSheetCode: '',
      termSheetCodeOptions: [],
      termSheetCode: '',
      options: {
        cprDelimiter: {
          delimiters: ['-'],
          blocks: [6, 4],
          uppercase: true,
        },
      },
      maxCharacters: 50,
      maxCharactersAllowed: 50,
    }
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },

    collectiontypeoptions() {
      const temp = [
        { text: i18n.t('DEBTIA_FOREIGN'), value: 'foreign' },
        { text: i18n.t('DEBTIA_OWN'), value: 'own' },
      ]

      return temp
    },
  },
  setup() {
    const blankUserData = {
      username: '',
      email: '',
      name: '',
      loginStatus: '1',
      phone: null,
      password: null,
      company: null,
      type: 'person',
      company_type: null,
      cvrcpr: null,
      cpr: null,
      birth_date: null,
      billing_email: null,
      termSheetCode: null,
      collection_type: 'foreign',
      rki_customer_id: null,
      address: {
        road_name: null,
        house_number: null,
        place: null,
        zip_code: null,
        city: null,
        country_id: 247,
      },
      billing_address: {
        road_name: null,
        house_number: null,
        place: null,
        zip_code: null,
        city: null,
        country_id: null,
      },
      bankBranchNo: null,
      bankAccountNo: null,
      iban: null,
      swiftCode: null,
      vat_exempted: false,
    }
    const userData = reactive(blankUserData)
    const resetuserData = () => {
      userData.value = JSON.parse(JSON.stringify(blankUserData))
    }

    const {
      refFormObserver,
      getValidationState,
      resetForm,
    } = formValidation(resetuserData)

    const loginOptions = [
      { label: 'Allowed', value: '1' },
      { label: 'Not Allowed', value: '0' },
    ]

    const typeOptions = [
      { label: 'Person', value: 'person' },
      { label: 'Sole Proprietorship', value: 'sole-proprietorship' },
      { label: 'Capital Company', value: 'capital-company' },
    ]

    const alertErrors = ref([])

    const validateUsername = ref(true)

    const loader = ref(false)
    const onSubmit = async () => {
      loader.value = true
      const data = { ...userData }
      const validation = await axios.post('clients/validate-username', { username: data.username })
        .then(() => true).catch(() => false)

      if (!validation) {
        validateUsername.value = validation
        loader.value = false
        return false
      }
      // this.loader = true
      const USER_APP_STORE_MODULE_NAME = 'app-client'

      // Register module
      if (!store.hasModule(USER_APP_STORE_MODULE_NAME)) store.registerModule(USER_APP_STORE_MODULE_NAME, clientStoreModule)

      store.dispatch('app-client/addClient', data)
        .then(() => {
          loader.value = true
          validateUsername.value = true
          router.replace('/clients/list')
            .then(() => {
              $this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: 'Client Added',
                  icon: 'CoffeeIcon',
                  variant: 'success',
                  text: 'Client successfully Added!',
                },
              })
            })
        })
        .catch(error => {
          alertErrors.value = []
          alertErrors.value.push(...error.data.message)
          loader.value = false
        })
      return true
    }

    return {
      loginOptions,
      refFormObserver,
      getValidationState,
      resetForm,
      userData,
      validateUsername,
      onSubmit,
      alertErrors,
      typeOptions,
      loader,
    }
  },
  watch: {
    selectedTermSheetCode: {
      handler() {
        this.userData.termSheetCode = this.selectedTermSheetCode
      },
    },
    selectedoMonitoringTermSheetCode: {
      handler() {
        this.userData.monitoring_termsheetcode = this.selectedoMonitoringTermSheetCode
      },
    },
  },
  created() {
    this.userData.username = this.$getRandomUsername()
    this.userData.password = this.$getRandomPassword()
    this.maxCharacters = 50 - this.$getRandomUsername().length
    this.getSettingValues('default_termsheetcode')
    this.getTermsheetCodes()
    this.createUsername()
  },
  methods: {
    addressRequired() {
      if (this.userData.billing_address.road_name
            || this.userData.billing_address.house_number
              || this.userData.billing_address.place
                || this.userData.billing_address.zip_code
                  || this.userData.billing_address.city
                    || this.userData.billing_address.country_id) {
        return true
      }
      return false
    },
    getCityName(type, event) {
      axios.get(`settings/get-city-name-by-code?code=${event}`)
        .then(response => {
          if (type === 'standard_address') {
            if (response.data.data.city_name !== null) {
              this.userData.address.city = response.data.data.city_name
            }
          }
          if (type === 'billing_address') {
            if (response.data.data.city_name !== null) {
              this.userData.billing_address.city = response.data.data.city_name
            }
          }
        }).catch(error => {
          console.log(error)
        })
    },
    getZipCode(type, event) {
      axios.get(`settings/get-zip-code-by-city-name?city=${event}`)
        .then(response => {
          if (type === 'standard_address') {
            if (response.data.data.zip_code !== null) {
              this.userData.address.zip_code = response.data.data.zip_code
            }
          }
          if (type === 'billing_address') {
            if (response.data.data.zip_code !== null) {
              this.userData.billing_address.zip_code = response.data.data.zip_code
            }
          }
        }).catch(error => {
          console.log(error)
        })
    },
    async getSettingValues(selected) {
      this.loader = true
      await axios.get('user-settings/get', { params: { key: selected } }).then(response => {
        const settingDetails = response.data.data
        if (settingDetails === null) {
          this.selectedTermSheetCode = ''
          this.selectedoMonitoringTermSheetCode = ''
        } else {
          this.selectedTermSheetCode = settingDetails.value
          this.selectedoMonitoringTermSheetCode = ''
        }
        this.loader = false
      }).catch(() => {
        this.getDefaultValues(selected)
        this.loader = false
      })
    },
    async getTermsheetCodes() {
      this.termSheetCodeLoader = true
      await axios.get('/settings/termsheetcode-dropdown').then(response => {
        if (response.status === 200) {
          const termsheetCodes = response.data.data
          this.termSheetCodeOptions = termsheetCodes
          this.termSheetCodeLoader = false
        }
      })
    },
    async createUsername() {
      const url = `${constants.baseUrl}/create-validate/username`
      await axios.post(url, {
        type: 'client',
      }).then(response => {
        if (response.status === 200) {
          this.userData.username = response.data.data.username
        }
      })
    },
    characterLeft(event) {
      this.maxCharacters = this.maxCharactersAllowed - event.length
    },
  },
}
</script>

<style lang="scss">
.custom-control.custom-control-inline.custom-radio {
  margin-top: 5px;
}
</style>
